import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const STRATEGY_ERRORS = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보를 확인할 수 없습니다.',
  union_manager_required: '전략판 수정 권한이 없습니다.',
  raid_season_not_found: '선택한 레이드 차수를 찾을 수 없습니다.',
  invalid_strategy_boss_no: '보스 번호를 확인해주세요.',
  invalid_strategy_phase_no: '단수 정보를 확인해주세요.',
  invalid_strategy_target_hp: '목표 체력을 확인해주세요.',
  invalid_strategy_items: '전략판 공격 슬롯 정보를 확인해주세요.',
  invalid_strategy_item: '전략판에 저장할 슬롯의 멤버·덱·딜량 정보를 확인해주세요.',
  invalid_strategy_item_number: '전략판 슬롯의 보스·슬롯·딜량 정보를 확인해주세요.',
  invalid_strategy_phase_plans: '단수별 5보스 계획 정보를 확인해주세요.',
  five_strategy_bosses_required: '1~5보스 계획을 모두 포함해야 합니다.',
  duplicate_strategy_practice_slot: '같은 모의전 슬롯이 현재 계획에 중복 배치되어 있습니다.',
  strategy_slot_already_assigned: '이미 다른 보스 또는 단수에 배치된 모의전 슬롯이 있습니다.',
  strategy_member_attack_limit_exceeded: '실전과 모든 단수의 계획을 합쳐 멤버당 3타를 초과할 수 없습니다.',
  strategy_member_nikke_reused: '같은 멤버의 3타 구성에서 동일한 니케를 다시 사용할 수 없습니다.',
  strategy_note_too_long: '전략판 설명이 너무 깁니다.',
  strategy_migration_required: '전략판 DB 적용 전 미리보기 상태입니다.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function strategyError(error, fallback) {
  const raw = String(error?.message || error?.details || '');
  const migrationMissing = /get_union_raid_strategy_plans|save_union_raid_strategy_plan_v2|save_union_raid_strategy_phase_v2|raid_strategy_plans/i.test(raw)
    && /does not exist|could not find|schema cache|pgrst202|pgrst205/i.test(`${raw} ${error?.code || ''}`);
  const code = migrationMissing
    ? 'strategy_migration_required'
    : Object.keys(STRATEGY_ERRORS).find((item) => raw.includes(item));
  const next = new Error(STRATEGY_ERRORS[code] || raw || fallback);
  next.code = code || error?.code || 'strategy_unknown_error';
  return next;
}

function normalizeItem(item = {}, index = 0) {
  return {
    practiceId: textValue(item.practiceId ?? item.practice_id),
    memberOpenid: textValue(item.memberOpenid ?? item.member_openid),
    nickname: textValue(item.nickname ?? item.memberNickname ?? item.member_nickname) || '닉네임 확인 불가',
    bossNo: numberValue(item.bossNo ?? item.boss_no),
    slotNo: numberValue(item.slotNo ?? item.slot_no, 1),
    title: textValue(item.title),
    damageEok: numberValue(item.damageEok ?? item.damage_eok),
    deck: Array.isArray(item.deck) ? item.deck : [],
    note: textValue(item.note),
    order: numberValue(item.order, index + 1),
  };
}

function serializeItems(items = [], bossNo = 0) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    practiceId: textValue(item.practiceId ?? item.id),
    memberOpenid: textValue(item.memberOpenid),
    nickname: textValue(item.nickname),
    bossNo: numberValue(item.bossNo, bossNo),
    slotNo: numberValue(item.slotNo, 1),
    title: textValue(item.title),
    damageEok: numberValue(item.damageEok),
    deck: Array.isArray(item.deck) ? item.deck : [],
    note: textValue(item.note),
    order: index + 1,
  }));
}

function serializePlan(plan = {}) {
  const bossNo = numberValue(plan.bossNo);
  return {
    bossNo,
    targetHpEok: Math.max(0, numberValue(plan.targetHpEok)),
    note: textValue(plan.note),
    items: serializeItems(plan.items, bossNo),
  };
}

export function normalizeStrategyPlan(plan = {}) {
  return {
    id: textValue(plan.id),
    bossNo: numberValue(plan.bossNo ?? plan.boss_no),
    phaseNo: numberValue(plan.phaseNo ?? plan.phase_no),
    targetHpEok: numberValue(plan.targetHpEok ?? plan.target_hp_eok),
    note: textValue(plan.note),
    items: (Array.isArray(plan.items) ? plan.items : [])
      .map(normalizeItem)
      .sort((left, right) => left.order - right.order),
    updatedAt: plan.updatedAt ?? plan.updated_at ?? null,
    updatedBy: textValue(plan.updatedBy ?? plan.updated_by),
  };
}

export async function loadRaidStrategyPlans(seasonNo = null) {
  const client = requireClient();
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const { data, error } = await client.rpc('get_union_raid_strategy_plans', {
    requested_season_no: requested,
  });
  if (error) throw strategyError(error, '전략판 계획을 불러오지 못했습니다.');
  return {
    unionId: textValue(data?.union_id),
    unionName: textValue(data?.union_name),
    viewerRole: textValue(data?.viewer_role),
    canManage: Boolean(data?.can_manage),
    migrationRequired: false,
    seasonId: textValue(data?.season_id),
    seasonNo: numberValue(data?.season_no),
    plans: (Array.isArray(data?.plans) ? data.plans : []).map(normalizeStrategyPlan),
  };
}

export async function saveRaidStrategyPlan({
  seasonNo,
  bossNo,
  phaseNo,
  targetHpEok = 0,
  note = '',
  items = [],
}) {
  const client = requireClient();
  const { data, error } = await client.rpc('save_union_raid_strategy_plan_v2', {
    target_season_no: Number(seasonNo),
    target_boss_no: Number(bossNo),
    target_phase_no: Number(phaseNo),
    target_hp_eok: Math.max(0, numberValue(targetHpEok)),
    plan_note: textValue(note),
    plan_items: serializeItems(items, bossNo),
  });
  if (error) throw strategyError(error, '전략판 계획을 저장하지 못했습니다.');
  return normalizeStrategyPlan(data?.plan || {});
}

export async function saveRaidStrategyPhase({ seasonNo, phaseNo, plans = [] }) {
  const client = requireClient();
  const payload = (Array.isArray(plans) ? plans : []).map(serializePlan);
  const { data, error } = await client.rpc('save_union_raid_strategy_phase_v2', {
    target_season_no: Number(seasonNo),
    target_phase_no: Number(phaseNo),
    phase_plans: payload,
  });
  if (error) throw strategyError(error, '단수별 전략판을 저장하지 못했습니다.');
  return (Array.isArray(data?.plans) ? data.plans : []).map(normalizeStrategyPlan);
}
